import {EventEmitter, Injectable, Output} from '@angular/core';
import {AppAuthService} from '@app/auth';
import {Capacitor} from '@capacitor/core';
import {tap} from 'rxjs/operators';
import {ApiService} from './api.service';
import {MOBILE_HOME_URL} from './mobile.constants';
import {getMobilePlanningUrl} from './mobile-planning.util';
import {hasAnyPagePermission, isGlobalPagePermission} from './page-access.util';

export interface Session {
	fournisseur: any;
	restaurant: any;
	wantedUrl?: string;
}

export interface RuntimeSession {
	permissions: object[];
	neededPermissions: string[] | null;
}


@Injectable({
	providedIn: 'root',
})
export class SessionService {
	public static readonly SESSION_KEY = 'app_session';
	private static readonly LEGACY_SESSION_KEY = 'groin_session';
	@Output() restaurantChange: EventEmitter<number> = new EventEmitter();
	@Output() permissionChange: EventEmitter<string> = new EventEmitter();
	@Output() permissionNeededChange: EventEmitter<string> = new EventEmitter();
	@Output() restaurantsCatalogRefresh: EventEmitter<void> = new EventEmitter();
	@Output() identityChange: EventEmitter<number | null> = new EventEmitter();
	runtimeSession: RuntimeSession;
	private lastTokenUserId: number | null = null;
	private permissionsLoaded = false;

	constructor(private authService: AppAuthService, private apiService: ApiService) {
		SessionService.getSession();
		this.authService.onTokenChange().subscribe((token) => {
			if (token.isValid()) {
				const userId = (token.getPayload()?.data as { id?: number } | undefined)?.id ?? null;
				const identityChanged = userId !== this.lastTokenUserId;
				this.lastTokenUserId = userId;
				this.reloadPermissionsAfterTokenChange(identityChanged);
			} else {
				this.lastTokenUserId = null;
				this.setPermissions([]);
			}
		});
	}

	public static setWantedUrl(url: string): void {
		const session = SessionService.getSession();
		session.wantedUrl = url;
		SessionService.setSession(session);
	}

	public static getWantedUrl(): string | null {
		const session = SessionService.getSession();
		if (session?.wantedUrl) {
			return session.wantedUrl;
		}
		return Capacitor.isNativePlatform() ? MOBILE_HOME_URL : '/pages/dashboard';
	}

	getMobilePlanningUrl(): string {
		return getMobilePlanningUrl(this);
	}

	/********** private **********/

	private static getSession(): Session {
		let raw = localStorage.getItem(SessionService.SESSION_KEY);
		if (!raw) {
			raw = localStorage.getItem(SessionService.LEGACY_SESSION_KEY);
			if (raw) {
				localStorage.setItem(SessionService.SESSION_KEY, raw);
				localStorage.removeItem(SessionService.LEGACY_SESSION_KEY);
			}
		}
		let session: Session = raw ? JSON.parse(raw) : null;

		if (!session) {
			session = {
				restaurant: null,
				fournisseur: null,
				wantedUrl: null,
			};
		}
		return session;
	}

	private static setSession(session: Session) {
		if (session == null) {
			return;
		}
		if (!navigator.cookieEnabled || !localStorage) {
			return;
		}
		localStorage.setItem(SessionService.SESSION_KEY, JSON.stringify(session));
	}

	arePermissionsLoaded(): boolean {
		return this.permissionsLoaded;
	}

	public loadPermissions(onLoaded?: () => void): void {
		this.permissionsLoaded = false;
		this.apiService.getPermissions().subscribe({
			next: (value) => {
				this.setPermissions(value);
				onLoaded?.();
			},
			error: () => {
				this.setPermissions([]);
				onLoaded?.();
			},
		});
	}

	private reloadPermissionsAfterTokenChange(identityChanged: boolean): void {
		this.permissionsLoaded = false;
		this.apiService.getPermissions().subscribe({
			next: (value) => {
				if (identityChanged) {
					this.identityChange.emit(this.lastTokenUserId);
				}
				this.setPermissions(value);
			},
			error: () => {
				if (identityChanged) {
					this.identityChange.emit(this.lastTokenUserId);
				}
				this.setPermissions([]);
			},
		});
	}

	getRestaurantChangeEmitter() {
		return this.restaurantChange;
	}

	getPermissionNeededChangeEmitter() {
		return this.permissionNeededChange;
	}

	getPermissionChangeEmitter() {
		return this.permissionChange;
	}

	getRestaurantsCatalogRefreshEmitter() {
		return this.restaurantsCatalogRefresh;
	}

	getIdentityChangeEmitter() {
		return this.identityChange;
	}

	notifyRestaurantsCatalogRefresh(): void {
		this.restaurantsCatalogRefresh.emit();
	}

	reapplyRestaurantFromSession(): void {
		const restaurant = this.getRestaurant();
		this.restaurantChange.emit(restaurant ? restaurant.id : null);
	}

	getWantedUrlCheckAuth() {
		return new Promise((resolve, reject) => {
			this.authService.isAuthenticated()
				.pipe(
					tap(authenticated => {
						if (authenticated) {
							const session = SessionService.getSession();
							resolve(session && session.wantedUrl ? session.wantedUrl : '/pages/dashboard');
						} else {
							resolve(null);
						}
					}),
				);
		});
	}

	getRestaurant() {
		const session = SessionService.getSession();
		return (session && session.restaurant) ? session.restaurant : null;
	}

	getRestaurantId() {
		return (this.getRestaurant() ? this.getRestaurant()['id'] : null);
	}

	setRestaurant(restaurant) {
		const session = SessionService.getSession();
		const previousId = this.normalizeRestaurantId(session?.restaurant?.id);
		const newId = this.normalizeRestaurantId(restaurant?.id);
		session.restaurant = restaurant;
		SessionService.setSession(session);
		if (previousId !== newId) {
			this.restaurantChange.emit(newId);
		}
	}

	private normalizeRestaurantId(restaurantId: unknown): number | null {
		if (restaurantId == null || restaurantId === '') {
			return null;
		}
		const parsed = parseInt(String(restaurantId), 10);
		return Number.isNaN(parsed) ? null : parsed;
	}

	getPermissions(): object[] {
		const session = this.getRuntimeSession();
		return (session && session.permissions) ? session.permissions : [];
	}

	getPermissionsRestaurant(restaurantId) {
		return this.getPermissions().find(value => {
			return value['restaurant_id'] === restaurantId;
		});
	}

	getPermissionRestaurant(restaurantId, permName) {
		const perm = this.getPermissionsRestaurant(restaurantId);
		if (!perm) {
			return false;
		}
		return (perm[permName] === 1);
	}

	setPermissions(permissions: object[]) {
		const session = this.getRuntimeSession();
		session.permissions = permissions;
		this.setRuntimeSession(session);
		this.permissionsLoaded = true;
		this.tryAutoSelectRestaurant();
		this.permissionChange.emit();
	}

	/** Sélectionne automatiquement le restaurant si l'utilisateur n'en a qu'un. */
	private tryAutoSelectRestaurant(): void {
		const restaurantIds = this.getDistinctRestaurantIdsFromPermissions();
		const currentId = this.getRestaurantId();

		if (restaurantIds.length === 1) {
			const onlyId = restaurantIds[0];
			if (currentId === onlyId) {
				return;
			}
			this.setRestaurant({id: onlyId});
			this.apiService.getRestaurant(onlyId).subscribe({
				next: (restaurant) => {
					if (this.getRestaurantId() === onlyId) {
						this.setRestaurant(restaurant);
					}
				},
			});
			return;
		}

		if (currentId != null && restaurantIds.length > 0 && !restaurantIds.includes(currentId)) {
			this.setRestaurant(null);
		}
	}

	private getDistinctRestaurantIdsFromPermissions(): number[] {
		const seen = new Set<number>();
		const ids: number[] = [];
		for (const permission of this.getPermissions()) {
			const restaurantId = parseInt(String(permission['restaurant_id']), 10);
			if (Number.isNaN(restaurantId) || seen.has(restaurantId)) {
				continue;
			}
			seen.add(restaurantId);
			ids.push(restaurantId);
		}
		return ids;
	}

	getNeededPermissions(): string | null {
		const list = this.getNeededPermissionsList();
		return list.length ? list[0] : null;
	}

	getNeededPermissionsList(): string[] {
		const session = this.getRuntimeSession();
		return session?.neededPermissions?.length ? [...session.neededPermissions] : [];
	}

	hasCurrentPageAccess(): boolean {
		const needed = this.getNeededPermissionsList();
		if (!needed.length) {
			return true;
		}
		const restaurantId = this.getRestaurantId();
		if (!restaurantId && needed.some((permission) => !isGlobalPagePermission(permission))) {
			return true;
		}
		return hasAnyPagePermission(this, needed);
	}

	hasPermission(permissionName, restaurantId = null) {
		let hasPerm = false;
		this.getPermissions().filter(obj => {
			if (restaurantId) {
				if (obj[permissionName] === 1 && obj['restaurant_id'] === restaurantId) {
					hasPerm = true;
					return;
				}
			} else {
				if (obj[permissionName] === 1) {
					hasPerm = true;
					return;
				}
			}
		});
		return hasPerm;
	}

	/** Consultation masse salariale (lecture seule ou édition). */
	canViewSalaryMass(restaurantId: number | null = null): boolean {
		const id = restaurantId ?? this.getRestaurantId();
		if (!id) {
			return this.hasPermission('salary') || this.hasPermission('salary_view');
		}
		return this.hasPermission('salary', id) || this.hasPermission('salary_view', id);
	}

	/** Modification des taux / masse salariale. */
	canEditSalaryMass(restaurantId: number | null = null): boolean {
		const id = restaurantId ?? this.getRestaurantId();
		if (!id) {
			return this.hasPermission('salary');
		}
		return this.hasPermission('salary', id);
	}

	/** Rôle Dieu sur au moins un atelier actif. */
	isDieu(): boolean {
		return this.getPermissions().some((permission) => Number(permission['id']) === 42);
	}

	/** Admin (1) ou Dieu (42) sur l'atelier : voit les réservations et plans de test. */
	canSeeRestaurantTest(restaurantId: number | null = null): boolean {
		const id = restaurantId ?? this.getRestaurantId();
		if (!id) {
			return false;
		}
		return this.getPermissions().some((permission) => {
			return Number(permission['restaurant_id']) === Number(id)
				&& (Number(permission['id']) === 1 || Number(permission['id']) === 42);
		});
	}

	/** Rôle franchisé : le nom du rôle contient « franchis » (ex. Gérant - Franchise, Franchisé en formation). */
	isFranchiseRole(restaurantId: number | null = null): boolean {
		const id = restaurantId ?? this.getRestaurantId();
		if (!id) {
			return false;
		}
		const perm = this.getPermissionsRestaurant(id) as { name?: string } | undefined;
		if (!perm?.name) {
			return false;
		}
		return perm.name.toLowerCase().includes('franchis');
	}

	getFranchiseRoleName(restaurantId: number | null = null): string | null {
		const id = restaurantId ?? this.getRestaurantId();
		if (!id) {
			return null;
		}
		const perm = this.getPermissionsRestaurant(id) as { name?: string } | undefined;
		return perm?.name ?? null;
	}

	/** Consultation fournisseurs / tarifs : stock + rôle franchisé. */
	canViewFournisseurTarifs(restaurantId: number | null = null): boolean {
		const id = restaurantId ?? this.getRestaurantId();
		if (!id || !this.hasPermission('stock', id)) {
			return false;
		}
		return this.isFranchiseRole(id);
	}

	/** UI demandes : stock + rôle franchisé sur le restaurant courant. */
	canUseFournisseurDemandes(): boolean {
		return this.canViewFournisseurTarifs();
	}

	/** Demandes popup résa : permission dédiée sur le restaurant courant. */
	canUseReservationPopupRequests(): boolean {
		const restaurantId = this.getRestaurantId();
		if (!restaurantId) {
			return false;
		}
		return this.hasPermission('reservation_event_request', restaurantId);
	}

	canReviewReservationPopupRequests(): boolean {
		return this.hasPermission('reservation_event_review');
	}

	canManageNationalReservationEvents(): boolean {
		return this.hasPermission('reservation_event_national');
	}

	canManageNationalEquipements(): boolean {
		return this.hasPermission('equipements_national');
	}

	canViewMffLinks(): boolean {
		return this.hasPermission('mff_links');
	}

	canManageReservationPopups(restaurantId: number | null = null): boolean {
		if (this.hasPermission('reservation_event_review')) {
			return true;
		}
		const id = restaurantId ?? this.getRestaurantId();
		if (id) {
			return this.hasPermission('reservation_event_manage', id);
		}
		return this.hasPermission('reservation_event_manage');
	}

	setNeededPermissions(neededPermissions: string | string[]) {
		const list = (Array.isArray(neededPermissions) ? neededPermissions : [neededPermissions])
			.filter((permission) => !!permission);
		const session = this.getRuntimeSession();
		const unchanged = session.neededPermissions?.length === list.length
			&& session.neededPermissions.every((permission, index) => permission === list[index]);
		if (unchanged) {
			return;
		}
		session.neededPermissions = list.length ? list : null;
		this.setRuntimeSession(session);
		this.permissionNeededChange.emit(list[0] ?? null);
	}

	clearNeededPermissions(): void {
		const session = this.getRuntimeSession();
		if (!session.neededPermissions?.length) {
			return;
		}
		session.neededPermissions = null;
		this.setRuntimeSession(session);
	}

	emptySession() {
		localStorage.removeItem(SessionService.SESSION_KEY);
		localStorage.removeItem(SessionService.LEGACY_SESSION_KEY);
	}

	private getRuntimeSession(): RuntimeSession {
		if (!this.runtimeSession) {
			this.runtimeSession = {
				neededPermissions: null,
				permissions: [],
			};
		}
		return this.runtimeSession;
	}

	private setRuntimeSession(session: RuntimeSession) {
		if (session == null) {
			return;
		}
		this.runtimeSession = session;
	}
}
